<script setup lang="ts">
/**
 * Glocke in der Kopfleiste mit dem Posteingang darunter (Spec §15).
 *
 * Ein Klick auf einen Hinweis führt dorthin, wo etwas passiert ist, und markiert ihn
 * dabei als gelesen — beides in einem Schritt: Wer erst lesen und dann abhaken muss,
 * hat am Ende einen Posteingang voller erledigter Dinge.
 *
 * Der Abruf läuft, solange die Glocke existiert — also solange jemand angemeldet ist
 * (sie steckt in der `AppShell`). Deshalb startet und stoppt sie ihn selbst, statt
 * dass eine entfernte Stelle daran denken muss.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { NotificationDto } from '@pmpro/shared';
import Icon from './Icon.vue';
import { zeitpunkt } from '../lib/aktivitaet';
import { hinweisUrheber, hinweisVorgang } from '../lib/benachrichtigungen';
import { useNotificationStore } from '../stores/notifications';

const store = useNotificationStore();
const router = useRouter();

const offen = ref(false);
const wurzel = ref<HTMLElement | null>(null);

onMounted(() => {
  store.starteAbruf();
  document.addEventListener('click', klickAusserhalb);
  document.addEventListener('keydown', taste);
});

onBeforeUnmount(() => {
  store.beendeAbruf();
  document.removeEventListener('click', klickAusserhalb);
  document.removeEventListener('keydown', taste);
});

function klickAusserhalb(event: MouseEvent): void {
  if (!offen.value) return;
  if (wurzel.value && !wurzel.value.contains(event.target as Node)) offen.value = false;
}

function taste(event: KeyboardEvent): void {
  if (event.key === 'Escape') offen.value = false;
}

function umschalten(): void {
  offen.value = !offen.value;
  // Beim Öffnen den aktuellen Stand holen: Zwischen zwei Abrufen liegt eine Minute,
  // und genau in diesem Moment sieht jemand hin.
  if (offen.value) void store.laden();
}

async function oeffnen(hinweis: NotificationDto): Promise<void> {
  offen.value = false;
  await store.alsGelesen(hinweis.id);
  await router.push({ name: 'feature', params: { id: hinweis.feature.id } });
}
</script>

<template>
  <div ref="wurzel" class="glocke">
    <button
      type="button"
      class="btn btn--ghost btn--sm glocke__knopf"
      :aria-expanded="offen"
      aria-haspopup="true"
      :aria-label="
        store.unread > 0 ? `Hinweise — ${store.unread} ungelesen` : 'Hinweise — nichts Neues'
      "
      @click="umschalten"
    >
      <Icon name="hinweise" :size="16" />
      <span v-if="store.unread > 0" class="glocke__zahl">{{ store.unread }}</span>
    </button>

    <div v-if="offen" class="panel" role="region" aria-label="Hinweise">
      <header class="panel__kopf">
        <span class="section-label">Hinweise</span>
        <button
          v-if="store.unread > 0"
          type="button"
          class="btn btn--ghost btn--sm"
          @click="store.allesGelesen()"
        >
          Alle gelesen
        </button>
      </header>

      <p v-if="store.items.length === 0" class="panel__leer muted">
        Nichts Neues — hier steht, was an deinen Features passiert.
      </p>

      <ul v-else class="liste">
        <li v-for="hinweis in store.items" :key="hinweis.id">
          <button
            type="button"
            class="eintrag"
            :class="{ 'eintrag--ungelesen': !hinweis.readAt }"
            @click="oeffnen(hinweis)"
          >
            <span class="eintrag__satz">
              <strong>{{ hinweisUrheber(hinweis) }}</strong>
              {{ hinweisVorgang(hinweis) }}
            </span>
            <span class="eintrag__feature">{{ hinweis.feature.title }}</span>
            <span v-if="hinweis.detail" class="eintrag__detail muted">{{ hinweis.detail }}</span>
            <span class="eintrag__zeit muted">{{ zeitpunkt(hinweis.createdAt) }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.glocke {
  position: relative;
}

.glocke__knopf {
  position: relative;
  padding: 0.35rem 0.5rem;
}

/* Die Zahl sitzt am Rand des Icons statt daneben: In der Kopfleiste zählt jeder
   Millimeter, und eine Ziffer an der Glocke liest man ohne hinzusehen. */
.glocke__zahl {
  position: absolute;
  top: -0.15rem;
  right: -0.15rem;
  min-width: 1.05rem;
  padding: 0 0.2rem;
  border-radius: var(--radius-pill);
  background: var(--color-error);
  color: var(--color-text-inverse);
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.05rem;
  text-align: center;
}

.panel {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 20;
  width: 22rem;
  max-height: 26rem;
  overflow-y: auto;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.panel__kopf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.panel__leer {
  padding: 0.5rem 0.25rem;
  font-size: 0.8125rem;
}

.liste {
  list-style: none;
  margin: 0;
  padding: 0;
}

.eintrag {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-radius: var(--radius);
  background: transparent;
  color: var(--color-text);
  text-align: left;
  font-size: 0.8125rem;
  line-height: 1.4;
}
.eintrag:hover {
  background: var(--color-surface-2);
}

/* Ungelesenes trägt einen farbigen Balken links — eine Fettschrift allein liest man
   in einer Liste aus lauter kurzen Zeilen nicht als Unterschied. */
.eintrag--ungelesen {
  border-left: 2px solid var(--color-primary);
  background: var(--color-primary-soft);
}
.eintrag--ungelesen:hover {
  background: var(--color-primary-soft);
}

.eintrag__feature {
  font-weight: 600;
}

.eintrag__detail {
  overflow-wrap: anywhere;
}

.eintrag__zeit {
  font-size: 0.75rem;
}
</style>
